export type PropertyType = "residential_rental" | "commercial" | "land" | "manual";

export type PropertyTypeOption = {
  value: PropertyType;
  label: string;
  recoveryYears: number | null;
};

export const PROPERTY_TYPES: readonly PropertyTypeOption[] = [
  { value: "residential_rental", label: "Residential rental real estate", recoveryYears: 27.5 },
  { value: "commercial", label: "Commercial / nonresidential real estate", recoveryYears: 39 },
  { value: "land", label: "Land / no depreciable building", recoveryYears: null },
  { value: "manual", label: "Manual annual depreciation", recoveryYears: null },
];

export type RealEstateSaleWorksheetInput = {
  propertyType: PropertyType;
  acquisitionDate: string;
  saleDate: string;
  purchasePrice: number;
  acquisitionCosts: number;
  improvements: number;
  sellingExpenses: number;
  sellingPrice: number;
  landValue: number;
  manualAnnualDepreciation: number;
  actualDepreciationOverride: number | null;
};

export type RealEstateSaleWorksheetResult = {
  recoveryYears: number | null;
  monthsHeld: number;
  yearsHeld: number;
  remainingMonths: number;
  costBasisBeforeDepreciation: number;
  netProceeds: number;
  gainOrLossBeforeDepreciation: number;
  depreciableBuildingValue: number;
  annualDepreciation: number;
  calculatedDepreciation: number;
  depreciationAllowedOrAllowable: number;
  adjustedBasis: number;
  totalGainOrLoss: number;
  depreciationRelatedGainReview: number;
  remainingGainOrLoss: number;
};

const nonNegative = (value: number) => Math.max(0, Number.isFinite(value) ? value : 0);

export function getMonthsHeld(acquisitionDate: string, saleDate: string): number {
  if (!acquisitionDate || !saleDate) return 0;
  const acquisition = new Date(`${acquisitionDate}T12:00:00`);
  const sale = new Date(`${saleDate}T12:00:00`);
  if (Number.isNaN(acquisition.getTime()) || Number.isNaN(sale.getTime()) || sale < acquisition) return 0;

  let months = (sale.getFullYear() - acquisition.getFullYear()) * 12 + sale.getMonth() - acquisition.getMonth();
  if (sale.getDate() < acquisition.getDate()) months -= 1;
  return Math.max(0, months);
}

export function calculateRealEstateSaleWorksheet(input: RealEstateSaleWorksheetInput): RealEstateSaleWorksheetResult {
  const property = PROPERTY_TYPES.find((option) => option.value === input.propertyType) ?? PROPERTY_TYPES[0]!;
  const purchasePrice = nonNegative(input.purchasePrice);
  const acquisitionCosts = nonNegative(input.acquisitionCosts);
  const improvements = nonNegative(input.improvements);
  const sellingExpenses = nonNegative(input.sellingExpenses);
  const sellingPrice = nonNegative(input.sellingPrice);
  const landValue = nonNegative(input.landValue);
  const monthsHeld = getMonthsHeld(input.acquisitionDate, input.saleDate);
  const recoveryYears = property.recoveryYears;
  const costBasisBeforeDepreciation = purchasePrice + acquisitionCosts + improvements;
  const netProceeds = sellingPrice - sellingExpenses;
  const gainOrLossBeforeDepreciation = netProceeds - costBasisBeforeDepreciation;
  const depreciableBuildingValue = input.propertyType === "land" ? 0 : Math.max(0, purchasePrice - landValue);
  const annualDepreciation = input.propertyType === "manual"
    ? nonNegative(input.manualAnnualDepreciation)
    : recoveryYears ? depreciableBuildingValue / recoveryYears : 0;
  const calculatedDepreciation = annualDepreciation * (monthsHeld / 12);
  const depreciationAllowedOrAllowable = input.propertyType === "land"
    ? 0
    : input.actualDepreciationOverride === null
      ? calculatedDepreciation
      : nonNegative(input.actualDepreciationOverride);
  const adjustedBasis = costBasisBeforeDepreciation - depreciationAllowedOrAllowable;
  const totalGainOrLoss = netProceeds - adjustedBasis;
  const depreciationRelatedGainReview = Math.min(depreciationAllowedOrAllowable, Math.max(0, totalGainOrLoss));

  return {
    recoveryYears,
    monthsHeld,
    yearsHeld: Math.floor(monthsHeld / 12),
    remainingMonths: monthsHeld % 12,
    costBasisBeforeDepreciation,
    netProceeds,
    gainOrLossBeforeDepreciation,
    depreciableBuildingValue,
    annualDepreciation,
    calculatedDepreciation,
    depreciationAllowedOrAllowable,
    adjustedBasis,
    totalGainOrLoss,
    depreciationRelatedGainReview,
    remainingGainOrLoss: totalGainOrLoss - depreciationRelatedGainReview,
  };
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}
